import React, { useState, useEffect } from "react";
import { usePWA } from "../hooks/usePWA";
import { usePWADismiss } from "../hooks/usePWADismiss";
import {
  Download,
  X,
  Smartphone,
  Monitor,
  CheckCircle,
  AlertCircle,
  Sparkles,
  Zap,
  Shield,
  Wifi,
  WifiOff,
} from "lucide-react";
import "./PWAInstallPrompt.css";

interface PWAInstallPromptProps {
  onClose?: () => void;
  showAlways?: boolean;
}

export const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({
  onClose,
  showAlways = false,
}) => {
  const {
    isInstallable,
    isInstalled,
    isOffline,
    hasUpdate,
    isStandalone,
    installPWA,
    applyUpdate,
  } = usePWA();

  const onDismiss = React.useCallback(() => {}, []);
  const onReset = React.useCallback(() => {}, []);

  const { isDismissed, dismiss, reset } = usePWADismiss({
    dismissDuration: 60 * 60 * 1000, // 1 hora
    onDismiss,
    onReset,
  });

  React.useEffect(() => {
    console.debug("PWAInstallPrompt mounted/render:", {
      isDismissed,
      isInstallable,
      isInstalled,
      isOffline,
      hasUpdate,
    });
  });

  const [isClient, setIsClient] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsClient(true);

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // useEffect para expor função de reset - sempre executado
  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as any).resetPWADismiss = reset;
    }
  }, [reset]);

  // useEffect para sincronizar estado de fechamento
  useEffect(() => {
    if (isDismissed) {
      setIsVisible(false);
    }
  }, [isDismissed]);

  // Função para forçar atualização completa (usada pelo UserProfile)
  const forceUpdate = React.useCallback(async () => {
    let reloaded = false;

    const reloadOnControllerChange = () => {
      if (reloaded) return;
      reloaded = true;
      setIsVisible(false);
      window.location.reload();
    };

    try {
      // Limpar cache primeiro
      if ("caches" in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map((cacheName) => caches.delete(cacheName))
        );
      }

      // Atualizar Service Worker com timeout
      if ("serviceWorker" in navigator) {
        try {
          // Adicionar timeout para evitar travamento
          const swUpdatePromise = (async () => {
            const registrations =
              await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) {
              await registration.update();
            }
          })();

          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error("Timeout no Service Worker")),
              3000
            )
          );

          await Promise.race([swUpdatePromise, timeoutPromise]);
        } catch (error) {
          console.warn("Erro ao atualizar Service Worker:", error);
        }
      }

      // Aplicar atualização com timeout
      try {
        const applyUpdatePromise = applyUpdate();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error("Timeout na aplicação de atualização")),
            2000
          )
        );

        const applied = await Promise.race([
          applyUpdatePromise,
          timeoutPromise,
        ]);

        // Fallback: se não houve SW "waiting" mas atualizou o registro, força reload
        if (!applied) {
          setTimeout(() => {
            if (!reloaded) {
              setIsVisible(false);
              window.location.reload();
            }
          }, 1000);
        }
      } catch (error) {
        console.warn("Erro ao aplicar atualização:", error);
        // Força reload mesmo se falhar
        setTimeout(() => {
          if (!reloaded) {
            setIsVisible(false);
            window.location.reload();
          }
        }, 1000);
      }
    } catch (error) {
      console.error("Erro ao forçar atualização:", error);
      setIsVisible(false);
      // Último recurso: recarregar para tentar pegar a versão nova
      window.location.reload();
    }
  }, [applyUpdate]);

  // Expor função globalmente para uso pelo UserProfile - sempre executado
  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as any).forcePWAUpdate = forceUpdate;
    }
  }, [forceUpdate]);

  // LÓGICA DE RENDERIZAÇÃO - APÓS TODOS OS HOOKS

  // Se não estiver no cliente, não renderiza
  if (!isClient) {
    return null;
  }

  // Se não pode ser instalado e não é para mostrar sempre, não renderiza
  if (!showAlways && !isInstallable && !hasUpdate && !isOffline) {
    return null;
  }

  // Se já está instalado, não mostra o prompt
  if (isInstalled && !hasUpdate) {
    return null;
  }

  // Se o modal foi fechado ou foi descartado, não renderiza
  if (!isVisible || isDismissed) {
    return null;
  }

  // FUNÇÕES DE HANDLER - definidas após as verificações

  const handleInstall = async () => {
    if (!isInstallable) return;

    setIsInstalling(true);
    try {
      const result = await installPWA();
    } catch (error) {
      console.error("Erro ao instalar:", error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleUpdate = async () => {
    let reloaded = false;

    const reloadOnControllerChange = () => {
      if (reloaded) return;
      reloaded = true;
      setIsVisible(false);
      window.location.reload();
    };

    try {
      if ("serviceWorker" in navigator) {
        // Recarrega assim que o novo SW assumir controle
        navigator.serviceWorker.addEventListener(
          "controllerchange",
          reloadOnControllerChange as EventListener,
          { once: true } as any
        );
      }

      const applied = await applyUpdate();

      // Fallback: se não houve SW "waiting" mas atualizou o registro, força reload
      if (!applied) {
        setTimeout(() => {
          if (!reloaded) {
            setIsVisible(false);
            window.location.reload();
          }
        }, 500);
      }
    } catch (error) {
      console.error("Erro ao aplicar atualização:", error);
      setIsVisible(false);
      // Último recurso: recarregar para tentar pegar a versão nova
      window.location.reload();
    }
  };

  const handleClose = () => {
    dismiss(); // Usa o hook para fechar
    if (onClose) onClose();
  };

  // Prompt de atualização
  if (hasUpdate) {
    return (
      <div className="fixed bottom-4 right-4 z-50 max-w-sm w-full bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-dynamic border border-blue-200/50 p-6 animate-slide-in-bottom">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="p-2 bg-blue-100 rounded-xl">
              <AlertCircle className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-2">
              <Sparkles className="h-4 w-4 text-blue-500" />
              <p className="text-sm font-semibold text-blue-900">
                Nova versão disponível
              </p>
            </div>
            <p className="text-sm text-blue-700 leading-relaxed">
              Uma nova versão do HelpDesk está disponível com melhorias e
              correções.
            </p>
            <div className="mt-4 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={handleUpdate}
                className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Zap className="h-4 w-4 mr-2" />
                Atualizar Agora
              </button>
              <button
                onClick={handleClose}
                className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2.5 border border-blue-200 text-sm font-medium rounded-xl text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
              >
                Depois
              </button>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="flex-shrink-0 p-1 text-blue-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-all duration-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  // Prompt de instalação
  if (isInstallable) {
    if (isMobile) {
      // Versão Mobile - Toast compacto na parte inferior
      return (
        <div className="fixed bottom-4 left-4 right-4 z-50 bg-gradient-to-r from-emerald-500 to-green-600 rounded-2xl shadow-dynamic border border-emerald-400/50 p-4 animate-slide-in-bottom mobile-optimized">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <Download className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">
                Instalar HelpDesk
              </p>
              <p className="text-xs text-emerald-100 leading-relaxed">
                Para uma experiência melhor
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleInstall}
                disabled={isInstalling}
                className="inline-flex items-center px-3 py-2 border border-white/30 text-xs font-medium rounded-xl text-white bg-white/20 hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isInstalling ? "..." : "Instalar"}
              </button>
              <button
                onClick={handleClose}
                className="p-1 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-all duration-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Versão Desktop - Toast compacto igual ao mobile
    return (
      <div className="fixed bottom-4 right-4 z-50 max-w-sm w-full bg-gradient-to-r from-emerald-500 to-green-600 rounded-2xl shadow-dynamic border border-emerald-400/50 p-4 animate-slide-in-bottom">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
              <Download className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">
              Instalar HelpDesk
            </p>
            <p className="text-xs text-emerald-100 leading-relaxed">
              Para uma experiência melhor
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleInstall}
              disabled={isInstalling}
              className="inline-flex items-center px-3 py-2 border border-white/30 text-xs font-medium rounded-xl text-white bg-white/20 hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isInstalling ? "..." : "Instalar"}
            </button>
            <button
              onClick={handleClose}
              className="p-1 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-all duration-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Indicador offline
  if (isOffline) {
    if (isMobile) {
      // Versão Mobile - Toast compacto
      return (
        <div className="fixed bottom-4 left-4 right-4 z-50 bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl shadow-dynamic border border-amber-400/50 p-4 animate-slide-in-bottom mobile-optimized">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <WifiOff className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">Modo Offline</p>
              <p className="text-xs text-amber-100 leading-relaxed">
                Algumas funcionalidades podem não estar disponíveis
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-1 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-all duration-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      );
    }

    // Versão Desktop - Toast compacto igual ao mobile
    return (
      <div className="fixed bottom-4 right-4 z-50 max-w-sm w-full bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl shadow-dynamic border border-amber-400/50 p-4 animate-slide-in-bottom">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
              <WifiOff className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">Modo Offline</p>
            <p className="text-xs text-amber-100 leading-relaxed">
              Algumas funcionalidades podem não estar disponíveis
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-1 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-all duration-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default PWAInstallPrompt;
